import React from "react";
import { Container, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { Settings } from "../../../../settings";
import { FONT_CONFIG } from "../../../../textureConfigs";

// Font sizing and text measurement using proven logic from FreeSpinMessageDisplay.jsx
const FontScaler = {
  // Measure text width using PIXI.TextMetrics (same approach as FreeSpinMessageDisplay)
  measureTextWidth: (text, fontSize, fontFamily = FONT_CONFIG.PRIMARY) => {
    const style = new PIXI.TextStyle({
      fontFamily,
      fontSize,
      fontWeight: "600",
    });

    const metrics = PIXI.TextMetrics.measureText(text, style);
    return metrics.width;
  },

  // Calculate optimal font size - adapted from FreeSpinMessageDisplay.jsx logic
  calculateOptimalFontSize: (
    text,
    containerWidth,
    baseFontSize = 32,
    device,
    orientation,
    symbolHeight
  ) => {
    if (!text || containerWidth <= 0) return baseFontSize;

    // Start with responsive base size (same as PixiBalanceDisplay pattern)
    let fontSize = baseFontSize;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontSize = baseFontSize * 1.5;
      } else {
        fontSize = baseFontSize * 1.4;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontSize = baseFontSize * 1.6;
      } else {
        fontSize = baseFontSize * 1.25;
      }
    }

    // Scale by symbol size (same pattern as other displays)
    const scale = symbolHeight / Settings.SYMBOL_HEIGHT;
    fontSize = Math.floor(fontSize * scale);

    // Set a reasonable minimum font size for readability (18px minimum)
    const absoluteMinFontSize = 18;

    // Test if text fits at current font size
    const textWidth = FontScaler.measureTextWidth(text, fontSize);

    // If text fits, use current size
    if (textWidth <= containerWidth) {
      return Math.max(fontSize, absoluteMinFontSize); // Ensure not below minimum
    }

    // Calculate shrink ratio needed (proportional scaling) - less aggressive shrinking
    const shrinkRatio = containerWidth / textWidth;
    // Only shrink if text is significantly too wide (more than 10% overflow)
    // and limit maximum shrink to 80% of original size
    const maxShrinkRatio = 0.8;
    const effectiveShrinkRatio = Math.max(shrinkRatio, maxShrinkRatio);
    const shrunkFontSize = Math.floor(fontSize * effectiveShrinkRatio);

    // Return the larger of calculated size or minimum size, but not below absolute minimum
    return Math.max(shrunkFontSize, absoluteMinFontSize);
  },
};

const ContentWindow = ({
  children,
  windowSize,
  name = "content_window",
  baseFontSize = 32, // Allow override of base font size
  title, // Optional title prop
  ...containerProps
}) => {
  const { device, orientation, symbolHeight } = useDimensions();

  // Use smart font scaling that measures text width
  const textContent = typeof children === "string" ? children : "";
  const availableWidth = windowSize?.width * 0.98 || 400; // Use more of available width

  const fontSize = FontScaler.calculateOptimalFontSize(
    textContent,
    availableWidth,
    baseFontSize, // Use provided base size or default 32
    device,
    orientation,
    symbolHeight
  );

  // Calculate title font size using same logic if title is provided
  const titleFontSize = title
    ? FontScaler.calculateOptimalFontSize(
        title,
        availableWidth,
        baseFontSize,
        device,
        orientation,
        symbolHeight
      )
    : fontSize;

  // Handle different content types
  const renderContent = () => {
    const elements = [];

    // Calculate positions for consistent spacing
    const titleTextHeight = title ? titleFontSize * 1.2 : 0; // Approximate text height with line spacing
    const contentTextHeight = fontSize * 1.2; // Approximate text height with line spacing
    const padding = Math.max(fontSize * 1.2, 32); // Even more generous padding for clear separation

    // Position elements with clear gap between title and content
    const titleY = title ? -(padding / 2 + contentTextHeight / 2) : 0;
    const contentY = title ? padding / 2 + titleTextHeight / 2 : -padding / 2;

    // Render title if provided
    if (title) {
      const titleStyle = new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: titleFontSize,
        fontWeight: "600",
        fill: "#ffbf11", // Yellow color from PixiBalanceDisplay
        align: "center",
        wordWrap: true,
        wordWrapWidth: windowSize?.width * 0.85 || 400,
      });

      elements.push(
        <Text
          key="title"
          text={title.toUpperCase()}
          style={titleStyle}
          anchor={0.5}
          y={titleY} // Position title above content with padding
        />
      );
    }

    // If children is a string, render as text with consistent styling
    if (typeof children === "string") {
      const textStyle = new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize,
        fontWeight: "600",
        fill: 0xffffff, // Always white (same as other displays)
        align: "center", // Always centered
        wordWrap: true, // Always enabled
        wordWrapWidth: windowSize?.width * 0.85 || 400, // Use 80% of window width for wrapping
      });

      elements.push(
        <Text
          key="content"
          text={children.toUpperCase()}
          style={textStyle}
          anchor={0.5}
          y={contentY} // Position content with consistent spacing
        />
      );
    } else {
      // If children is a React component/PIXI element, render as-is
      elements.push(
        <Container key="content" y={contentY}>
          {children}
        </Container>
      );
    }

    return elements;
  };

  return (
    <Container name={name} {...containerProps}>
      {renderContent()}
    </Container>
  );
};

export default ContentWindow;
